import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaExclamationCircle } from 'react-icons/fa';
import { noticeAPI } from '../utils/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

/**
 * NoticeWrite — 공지사항 작성 & 수정 페이지
 * - /notice/write     : 새 공지 작성
 * - /notice/edit/:id  : 기존 공지 수정
 */
const NoticeWrite = () => {
  const navigate = useNavigate();
  const { id } = useParams();          // 수정 시 공지 id
  const isEdit = !!id;

  const [title, setTitle] = useState('');
  const [contents, setContents] = useState('');
  const [isImportant, setIsImportant] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  // 수정 모드: 기존 데이터 로드
  useEffect(() => {
    if (!isEdit) return;
    const loadNotice = async () => {
      setFetchLoading(true);
      try {
        const res = await noticeAPI.getById(id);
        const n = res.data;
        setTitle(n.title || '');
        setContents(n.contents || '');
        setIsImportant(n.is_important || false);
      } catch (err) {
        setError('공지사항을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setFetchLoading(false);
      }
    };
    loadNotice();
  }, [id, isEdit]);

  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = '제목을 입력해주세요.';
    const plainText = contents.replace(/<[^>]+>/g, '').trim();
    if (!plainText) errs.contents = '내용을 입력해주세요.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError(null);
    try {
      const payload = {
        title: title.trim(),
        contents: contents.trim(),
        is_important: isImportant,
      };
      if (isEdit) {
        await noticeAPI.patch(id, payload);
      } else {
        await noticeAPI.create(payload);
      }
      navigate('/notice');
    } catch (err) {
      const serverMsg = err.response?.data;
      if (serverMsg && typeof serverMsg === 'object') {
        const msgs = Object.entries(serverMsg)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('\n');
        setError(msgs);
      } else {
        setError('저장 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nw-wrap">
      {/* ── 히어로 ── */}
      <section className="nw-hero">
        <div className="nw-hero-dharma" aria-hidden="true">☸</div>
        <Container className="nw-hero-inner">
          <span className="nw-badge">공지사항</span>
          <h1 className="nw-hero-title">{isEdit ? '공지 수정' : '공지 작성'}</h1>
          <p className="nw-hero-sub">
            {isEdit ? '공지사항 내용을 수정합니다.' : '새 공지사항을 작성합니다.'}
          </p>
        </Container>
      </section>

      <Container className="nw-container">
        {fetchLoading ? (
          <div className="nw-loading">
            <span className="nw-loading-dharma">☸</span>
            <p>불러오는 중…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="nw-form" noValidate>

            {/* 서버 에러 */}
            {error && (
              <div className="nw-server-error">
                <FaExclamationCircle /> {error}
              </div>
            )}

            {/* 중요 공지 토글 */}
            <div className="nw-field">
              <label className="nw-toggle-label">
                <div className={`nw-toggle ${isImportant ? 'nw-toggle--on' : ''}`}
                  onClick={() => setIsImportant(v => !v)}
                  role="switch"
                  aria-checked={isImportant}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === ' ' && setIsImportant(v => !v)}
                >
                  <span className="nw-toggle-thumb" />
                </div>
                <span className="nw-toggle-text">
                  {isImportant
                    ? <><span className="nw-important-badge"><FaExclamationCircle /> 중요 공지</span> 설정됨</>
                    : '중요 공지로 설정'}
                </span>
              </label>
            </div>

            {/* 제목 */}
            <div className="nw-field">
              <label className="nw-label" htmlFor="nw-title">
                제목 <span className="nw-required">*</span>
              </label>
              <input
                id="nw-title"
                type="text"
                className={`nw-input ${errors.title ? 'nw-input--error' : ''}`}
                placeholder="공지사항 제목을 입력하세요"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setErrors(p => ({...p, title: ''})); }}
                maxLength={200}
              />
              {errors.title && <p className="nw-field-error">{errors.title}</p>}
              <p className="nw-field-hint">{title.length} / 200자</p>
            </div>

            {/* 내용 (ReactQuill) */}
            <div className="nw-field">
              <label className="nw-label" htmlFor="nw-contents">
                내용 <span className="nw-required">*</span>
              </label>
              <div className={`nw-quill-wrap ${errors.contents ? 'nw-quill-error' : ''}`}>
                <ReactQuill 
                  theme="snow"
                  value={contents}
                  onChange={(val) => { setContents(val); setErrors(p => ({...p, contents: ''})); }}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                      [{'list': 'ordered'}, {'list': 'bullet'}],
                      ['link', 'image', 'video'],
                      ['clean']
                    ]
                  }}
                  formats={[
                    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
                    'list', 'bullet', 'link', 'image', 'video'
                  ]}
                  placeholder="공지사항 내용을 입력하세요"
                />
              </div>
              {errors.contents && <p className="nw-field-error">{errors.contents}</p>}
            </div>

            {/* 버튼 */}
            <div className="nw-actions">
              <button
                type="button"
                className="nw-btn nw-btn--ghost"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                <FaArrowLeft /> 취소
              </button>
              <button
                type="submit"
                className="nw-btn nw-btn--primary"
                disabled={loading}
              >
                <FaSave /> {loading ? '저장 중…' : (isEdit ? '수정 완료' : '공지 등록')}
              </button>
            </div>
          </form>
        )}
      </Container>

      <style jsx="true">{`
        :root {
          --nw-teal: #009590;
          --nw-teal-dark: #007873;
          --nw-teal-pale: #e8f5f5;
          --nw-text: #2c3e35;
          --nw-muted: #6e8a84;
          --nw-border: #e4edec;
        }

        /* 히어로 */
        .nw-hero {
          position: relative;
          background: linear-gradient(160deg, #f0fafa 0%, #e4f5f4 60%, #d5efee 100%);
          padding: 56px 0 48px;
          text-align: center;
          overflow: hidden;
        }
        .nw-hero-dharma {
          position: absolute;
          font-size: 14rem;
          color: var(--nw-teal);
          opacity: 0.04;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          line-height: 1;
        }
        .nw-hero-inner { position: relative; z-index: 2; }
        .nw-badge {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          color: var(--nw-teal);
          text-transform: uppercase;
          margin-bottom: 10px;
          padding: 4px 12px;
          border: 1px solid var(--nw-teal);
          border-radius: 2px;
        }
        .nw-hero-title {
          font-size: 2.2rem;
          font-weight: 700;
          color: var(--nw-teal);
          letter-spacing: -0.02em;
          margin: 8px 0 10px;
        }
        .nw-hero-sub { font-size: 0.92rem; color: var(--nw-muted); margin: 0; }

        /* 컨테이너 */
        .nw-container {
          padding-top: 36px;
          padding-bottom: 72px;
          max-width: 800px;
        }

        /* 로딩 */
        .nw-loading {
          text-align: center;
          padding: 80px 0;
          color: var(--nw-muted);
        }
        .nw-loading-dharma {
          font-size: 2.8rem;
          display: block;
          margin-bottom: 14px;
          color: var(--nw-teal);
          opacity: 0.25;
          animation: nwSpin 6s linear infinite;
        }
        @keyframes nwSpin { to { transform: rotate(360deg); } }

        /* 폼 */
        .nw-form { display: flex; flex-direction: column; gap: 24px; }

        .nw-server-error {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 14px 18px;
          background: #fff8f8;
          border: 1px solid #f5c6cb;
          border-left: 3px solid #e74c3c;
          border-radius: 4px;
          color: #c0392b;
          font-size: 0.88rem;
          white-space: pre-wrap;
        }

        /* 필드 */
        .nw-field { display: flex; flex-direction: column; gap: 6px; }
        .nw-label {
          font-size: 0.88rem;
          font-weight: 600;
          color: var(--nw-text);
        }
        .nw-required { color: #e74c3c; margin-left: 2px; }

        .nw-input {
          width: 100%;
          border: 1px solid var(--nw-border);
          border-radius: 4px;
          padding: 12px 14px;
          font-size: 0.92rem;
          color: var(--nw-text);
          outline: none;
          font-family: 'Noto Sans KR', sans-serif;
          transition: border-color 0.2s, box-shadow 0.2s;
          background: #fff;
        }
        .nw-input:focus {
          border-color: var(--nw-teal);
          box-shadow: 0 0 0 3px rgba(0, 149, 144, 0.08);
        }
        .nw-input--error { border-color: #e74c3c !important; }
        
        /* Quill 에디터 스타일링 */
        .nw-quill-wrap .quill { background: #fff; }
        .nw-quill-wrap .ql-toolbar {
          border-color: var(--nw-border);
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
          font-family: 'Noto Sans KR', sans-serif;
        }
        .nw-quill-wrap .ql-container {
          border-color: var(--nw-border);
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
          min-height: 350px;
          font-size: 0.95rem;
          font-family: 'Noto Sans KR', sans-serif;
        }
        .nw-quill-wrap .ql-editor {
          min-height: 350px;
          line-height: 1.7;
        }
        .nw-quill-wrap.nw-quill-error .ql-toolbar,
        .nw-quill-wrap.nw-quill-error .ql-container {
          border-color: #e74c3c;
        }

        .nw-field-error { font-size: 0.8rem; color: #e74c3c; margin: 0; }
        .nw-field-hint { font-size: 0.78rem; color: var(--nw-muted); margin: 0; }

        /* 중요 공지 토글 */
        .nw-toggle-label {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          user-select: none;
        }
        .nw-toggle {
          width: 46px;
          height: 26px;
          border-radius: 13px;
          background: #d1d5db;
          position: relative;
          transition: background 0.25s;
          cursor: pointer;
          flex-shrink: 0;
        }
        .nw-toggle--on { background: var(--nw-teal); }
        .nw-toggle-thumb {
          position: absolute;
          top: 3px; left: 3px;
          width: 20px; height: 20px;
          border-radius: 50%;
          background: #fff;
          transition: transform 0.25s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
        }
        .nw-toggle--on .nw-toggle-thumb { transform: translateX(20px); }
        .nw-toggle-text { font-size: 0.88rem; color: var(--nw-text); }
        .nw-important-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.7rem;
          font-weight: 700;
          color: #c0392b;
          background: #fdecea;
          border: 1px solid #f5c6cb;
          padding: 2px 8px;
          border-radius: 2px;
          margin-right: 4px;
        }

        /* 미리보기 */
        .nw-preview {
          border: 1px solid var(--nw-border);
          border-radius: 4px;
          overflow: hidden;
        }
        .nw-preview-label {
          padding: 10px 16px;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--nw-muted);
          background: #f8fbfb;
          border-bottom: 1px solid var(--nw-border);
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .nw-preview-body {
          padding: 20px 24px;
          font-size: 0.92rem;
          color: var(--nw-text);
          line-height: 1.85;
        }
        .nw-preview-body p { margin-bottom: 0.9rem; }
        .nw-preview-body h3, .nw-preview-body h4 {
          font-weight: 600;
          margin: 1.2rem 0 0.6rem;
        }

        /* 버튼 */
        .nw-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding-top: 8px;
          border-top: 1px solid var(--nw-border);
        }
        .nw-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 11px 24px;
          border-radius: 4px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          font-family: 'Noto Sans KR', sans-serif;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .nw-btn--primary { background: var(--nw-teal); color: #fff; }
        .nw-btn--primary:hover:not(:disabled) { background: var(--nw-teal-dark); }
        .nw-btn--ghost { background: #f3f4f6; color: #555; }
        .nw-btn--ghost:hover:not(:disabled) { background: #e5e7eb; }
        .nw-btn:disabled { opacity: 0.55; cursor: not-allowed; }

        /* 반응형 */
        @media (max-width: 640px) {
          .nw-hero { padding: 40px 0 32px; }
          .nw-hero-title { font-size: 1.8rem; }
          .nw-container { padding-top: 24px; padding-bottom: 48px; }
          .nw-actions { flex-direction: column; }
          .nw-btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default NoticeWrite;
